import { connectivity } from '../lib/connectivity';
import type { SyncEngine, SyncSummary } from './sync';

export interface SchedulerStatus {
  state: 'idle' | 'syncing' | 'error';
  online: boolean;
  lastSuccessAt: Date | null;
  lastError: string | null;
  failures: number;
  lastSummary: SyncSummary | null;
  /**
   * Das Gerät wurde abgekoppelt oder gesperrt (`DEVICE_REVOKED`, E11): Der Sync ist angehalten, die Warteschlange bleibt
   * unverändert erhalten. „Erneut versuchen“ (`retry`) startet ihn wieder (z. B. nach „Nur noch hochladen“).
   */
  revoked?: boolean;
}

/** Fehler der Gerätesitzung: abgekoppelt, gesperrt oder abgelaufen (Fehlercode `DEVICE_REVOKED`). */
const isDeviceRevoked = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'code' in error && error.code === 'DEVICE_REVOKED';

export interface SchedulerOptions {
  /** Abstand der Läufe bei funktionierendem Netz. */
  intervalMs?: number;
  minBackoffMs?: number;
  maxBackoffMs?: number;
  random?: () => number;
  now?: () => Date;
  isOnline?: () => boolean;
  onStatus?: (status: SchedulerStatus) => void;
  /** Das Gerät war beim letzten Mal abgekoppelt (nach Neuladen): gesperrt bis zum ersten erfolgreichen Sync. */
  initialRevoked?: boolean;
}

/**
 * Steuert den Sync: regelmäßig, sobald ein Netz da ist und auf Knopfdruck. Bei Fehlern wiederholt sie mit Backoff und
 * Zufallsversatz (2 s, 4 s, … bis 60 s), damit viele Kassen ein schwaches Netz nicht gemeinsam überlasten (SYN-04). Es läuft
 * nie mehr als ein Lauf gleichzeitig.
 */
export class SyncScheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  /** Ein Anstoß während eines laufenden Laufs: danach sofort noch einmal (neue Verkäufe warten nicht bis zum Takt). */
  private again = false;
  /** Ein Anstoß von außen (`nudge`) kam während eines Laufs: Auch nach einem Fehler folgt sofort ein Versuch, ohne Backoff. */
  private nudged = false;
  private stopped = true;
  private readonly interval: number;
  private readonly minBackoff: number;
  private readonly maxBackoff: number;
  private readonly random: () => number;
  private readonly now: () => Date;
  private readonly isOnline: () => boolean;
  private readonly onStatus: (status: SchedulerStatus) => void;
  private current: SchedulerStatus;

  constructor(
    private readonly engine: SyncEngine,
    options: SchedulerOptions = {},
  ) {
    this.interval = options.intervalMs ?? 10_000;
    this.minBackoff = options.minBackoffMs ?? 2_000;
    this.maxBackoff = options.maxBackoffMs ?? 60_000;
    this.random = options.random ?? Math.random;
    this.now = options.now ?? (() => new Date());
    this.isOnline = options.isOnline ?? (() => connectivity.get() !== 'offline');
    this.onStatus = options.onStatus ?? (() => undefined);
    this.current = {
      state: 'idle',
      online: this.isOnline(),
      lastSuccessAt: null,
      lastError: null,
      failures: 0,
      lastSummary: null,
      ...(options.initialRevoked === true ? { revoked: true } : {}),
    };
  }

  get status(): SchedulerStatus {
    return this.current;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.trigger();
  }

  /** Nach „abgekoppelt“ erneut versuchen (z. B. wenn die Leitung „Nur noch hochladen“ erlaubt hat). */
  retry(): void {
    this.stopped = false;
    this.update({ revoked: false, failures: 0 });
    void this.trigger();
  }

  /**
   * Netz ist (vermutlich) wieder da: Sofort senden, ohne den Backoff abzuwarten. Läuft nichts, startet sofort ein Lauf und der
   * Zeitgeber wird verworfen; `failures` bleibt für die Anzeige. Läuft schon ein Lauf, folgt gleich danach genau ein weiterer
   * (auch nach einem Fehler), nie zwei gleichzeitig. Nach „abgekoppelt“ oder bei angehaltenem Sync geschieht nichts.
   */
  nudge(): void {
    if (this.stopped || this.current.revoked === true) return;
    if (this.running) {
      this.again = true;
      this.nudged = true;
      return;
    }
    void this.trigger();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  /**
   * Sofort einen Lauf versuchen („Jetzt synchronisieren“, neuer Verkauf, Netz wieder da). Läuft schon einer, wird gleich danach
   * ein weiterer gestartet, nie zwei gleichzeitig.
   */
  async trigger(): Promise<void> {
    if (this.running) {
      this.again = true;
      return;
    }
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.running = true;
    this.update({ state: 'syncing', online: this.isOnline() });
    let delay = this.interval;
    try {
      const summary = await this.engine.runOnce();
      // Ein von Hand angestoßener Lauf („Jetzt synchronisieren“) nach „abgekoppelt“ gelang: Der Takt läuft wieder
      const wasRevoked = this.current.revoked === true;
      if (wasRevoked) this.stopped = false;
      this.update({
        ...(wasRevoked ? { revoked: false } : {}),
        state: 'idle',
        online: true,
        lastSuccessAt: this.now(),
        lastError: null,
        failures: 0,
        lastSummary: summary,
      });
    } catch (error) {
      if (isDeviceRevoked(error)) {
        // Nichts wird gelöscht oder wiederholt: Die Verkäufe bleiben in der Warteschlange, bis der Veranstalter entscheidet
        this.stopped = true;
        this.running = false;
        this.update({
          state: 'error',
          online: this.isOnline(),
          lastError: error instanceof Error ? error.message : 'Gerät abgekoppelt',
          failures: this.current.failures + 1,
          revoked: true,
        });
        return;
      }
      const failures = this.current.failures + 1;
      this.update({
        state: 'error',
        online: this.isOnline(),
        lastError: error instanceof Error ? error.message : 'Unbekannter Fehler',
        failures,
      });
      delay = this.backoff(failures);
    } finally {
      this.running = false;
    }
    if (this.again && !this.stopped) {
      const forced = this.nudged;
      this.again = false;
      this.nudged = false;
      // Nach einem Fehler nicht sofort wiederholen: der Backoff gilt (außer nach einem Anstoß „Netz ist wieder da“)
      if (this.current.state !== 'error' || forced) {
        await this.trigger();
        return;
      }
    }
    this.again = false;
    this.nudged = false;
    if (!this.stopped) {
      this.timer = setTimeout(() => {
        void this.trigger();
      }, delay);
    }
  }

  /** 2 s, 4 s, 8 s … bis höchstens 60 s, dazu bis zu 25 % Zufallsversatz. */
  backoff(failures: number): number {
    const base = Math.min(this.maxBackoff, this.minBackoff * 2 ** Math.max(0, failures - 1));
    return Math.round(base * (1 + this.random() * 0.25));
  }

  private update(patch: Partial<SchedulerStatus>): void {
    this.current = { ...this.current, ...patch };
    this.onStatus(this.current);
  }
}
